import React from 'react';
import { Target, User, Sparkles, GitBranch, PenTool, AlertTriangle, CheckCircle } from 'lucide-react';

const DimensionScoreBreakdown = ({ dimensionScores, authenticityCheck, memorabilityAssessment }) => {
  if (!dimensionScores) return null;

  const dimensions = [
    {
      key: 'contentSubstance',
      label: 'Content & Substance',
      icon: Target,
      color: 'azure',
      description: 'Meaningful insights about who you are'
    },
    {
      key: 'authenticityVoice',
      label: 'Authenticity & Voice',
      icon: User,
      color: 'violet',
      description: 'Genuine teenage voice, not overly polished'
    },
    {
      key: 'impactMemorability',
      label: 'Impact & Memorability',
      icon: Sparkles,
      color: 'amber',
      description: 'Will admissions remember this?'
    },
    {
      key: 'structureFlow',
      label: 'Structure & Flow',
      icon: GitBranch,
      color: 'emerald',
      description: 'Organization and transitions'
    },
    {
      key: 'writingQuality',
      label: 'Writing Quality',
      icon: PenTool,
      color: 'indigo',
      description: 'Grammar, word choice, mechanics'
    }
  ];

  const getScoreColor = (score, maxScore) => {
    const percentage = (score / maxScore) * 100;
    if (percentage >= 80) return 'text-emerald-600';
    if (percentage >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getProgressBarColor = (score, maxScore) => {
    const percentage = (score / maxScore) * 100;
    if (percentage >= 80) return 'bg-emerald-500';
    if (percentage >= 60) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  // Count authenticity red flags
  const redFlags = authenticityCheck ? [
    { key: 'thesaurusSyndrome', label: 'Thesaurus Syndrome' },
    { key: 'adverbOveruse', label: 'Adverb Overuse' },
    { key: 'melodramatic', label: 'Melodramatic' },
    { key: 'selfAggrandizing', label: 'Self-Aggrandizing' },
    { key: 'adultGhostwritten', label: 'Sounds Adult-Written' },
    { key: 'genericQuotes', label: 'Generic Quotes' }
  ].filter(flag => authenticityCheck[flag.key]) : [];

  return (
    <div className="bg-white rounded-lg border border-stone-200 p-6 space-y-6">
      {/* Header */}
      <div className="border-b pb-4">
        <h3 className="text-lg font-semibold text-slate-900 mb-2">Score Breakdown</h3>
        <p className="text-sm text-slate-600">
          Your essay is evaluated across five key dimensions that matter to admissions officers.
        </p>
      </div>

      {/* Dimension Scores */}
      <div className="space-y-4">
        {dimensions.map(({ key, label, icon: Icon, color, description }) => {
          const dimension = dimensionScores[key];
          if (!dimension) return null;
          
          const percentage = (dimension.score / dimension.maxScore) * 100;
          
          return (
            <div key={key} className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Icon className={`w-4 h-4 text-${color}-600`} />
                  <span className="font-medium text-slate-900">{label}</span>
                </div>
                <span className={`font-semibold ${getScoreColor(dimension.score, dimension.maxScore)}`}>
                  {dimension.score}/{dimension.maxScore}
                </span>
              </div>
              
              {/* Progress Bar */}
              <div className="w-full bg-stone-200 rounded-full h-2">
                <div 
                  className={`h-2 rounded-full transition-all ${getProgressBarColor(dimension.score, dimension.maxScore)}`}
                  style={{ width: `${percentage}%` }}
                />
              </div>
              
              {/* Description */}
              <p className="text-xs text-stone-500">{description}</p>
              
              {/* Feedback */}
              {dimension.feedback && (
                <div className="bg-stone-50 rounded p-3 text-sm text-slate-700">
                  {dimension.feedback}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Authenticity Check */}
      {authenticityCheck && (
        <div className="border-t pt-4">
          <h4 className="font-medium text-slate-900 mb-3 flex items-center gap-2">
            {authenticityCheck.soundsLikeTeenager ? (
              <>
                <CheckCircle className="w-4 h-4 text-emerald-600" />
                Authenticity Check: Passed
              </>
            ) : (
              <>
                <AlertTriangle className="w-4 h-4 text-yellow-600" />
                Authenticity Check: Concerns
              </>
            )}
          </h4>
          
          {redFlags.length > 0 && (
            <div className="space-y-2 mb-3">
              <p className="text-sm text-slate-600">Red flags detected:</p>
              <div className="flex flex-wrap gap-2">
                {redFlags.map(flag => (
                  <span key={flag.key} className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded-full">
                    {flag.label}
                  </span>
                ))}
              </div>
            </div>
          )}
          
          {authenticityCheck.overallAuthenticity && (
            <p className="text-sm text-slate-700 italic">
              "{authenticityCheck.overallAuthenticity}"
            </p>
          )}
        </div>
      )}

      {/* Memorability Assessment */}
      {memorabilityAssessment && (
        <div className="border-t pt-4">
          <h4 className="font-medium text-slate-900 mb-2 flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-amber-600" />
            Memorability Assessment
          </h4>
          <p className="text-sm text-slate-700 italic">
            "{memorabilityAssessment}"
          </p>
        </div>
      )}
    </div>
  );
};

export default DimensionScoreBreakdown;